// @ts-check

export const columnSettings = {
  name: {
    name: 'name',
    class: 'column-sortable text-left',
    stSort: 'name',
    stSkipNatural: true,
    displayName: 'PING_TREES_NAME'
  },
  revenueDynamic: {
    name: 'revenueDynamic',
    class: 'column-sortable text-left',
    stSort: 'revenueSettings.revenueDynamic',
    stSkipNatural: true,
    displayName: 'PING_TREES_REVENUE_DYNAMIC_TO_ROUTE'
  },
  minRevenueToRoute: {
    name: 'minRevenueToRoute',
    class: 'column-sortable text-right',
    stSort: 'minRevenueToRoute',
    stSkipNatural: true,
    displayName: 'PING_TREES_MIN_REVENUE_TO_ROUTE',
    rowAlign: 'right'
  },
  revenueOnFailure: {
    name: 'revenueOnFailure',
    class: 'column-sortable text-right',
    stSort: 'revenueSettings.revenueOnFailure',
    stSkipNatural: true,
    displayName: 'PING_TREES_REVENUE_ON_FAILURE',
    rowAlign: 'right'
  },
  numTargets: {
    name: 'numTargets',
    class: 'column-sortable text-right',
    stSort: 'targets.length',
    displayName: 'PING_TREES_TARGETS',
    stSkipNatural: true,
    rowAlign: 'right'
  },
  enabled: {
    name: 'enabled',
    class: 'tablesort-sortable text-center',
    stSort: 'enabled',
    displayName: 'PING_TREES_STATUS',
    rowAlign: 'center'
  }
};

export const defaultColumns = [
  'name',
  'revenueDynamic',
  'minRevenueToRoute',
  'revenueOnFailure',
  'numTargets',
  'enabled'
];

export default class PublisherService {
  /**
	 * @ngInject
	 * @param {import('angular').IHttpService} $http
	 * @param {*} ngAuthSettings
	 * @param {import('lodash').LoDashStatic} _
	 */

  constructor(
    _,
    $http,
    concurrencyMatrixService,
    ngAuthSettings,
    statsService,
    pingTreeTargetService
  ) {
    this._ = _;
    this.$http = $http;
    this.concurrencyMatrixService = concurrencyMatrixService;
    this.ngAuthSettings = ngAuthSettings;
    this.serviceBaseUri = ngAuthSettings.apiServiceBaseUri;
    this.statsService = statsService;
    this.pingTreeTargetService = pingTreeTargetService;
    this.columnSettings = _.cloneDeep(columnSettings);
    this.defaultColumns = _.cloneDeep(defaultColumns);
  }

  _serviceBaseUrl(accountId, pingTreeId) {
    return `${this.serviceBaseUri}${accountId}/pingtrees/${pingTreeId}`;
  }

  _transformConcurrencyMatrixFromResponse(response) {
    const pingTree = response.data.pingTree;
    if (
      typeof pingTree.schedule !== 'undefined' &&
			pingTree.schedule.callConcurrencyMatrix
    ) {
      pingTree.schedule.callConcurrencyMatrix = this.concurrencyMatrixService.getProcessMatrix(
        pingTree.schedule.callConcurrencyMatrix
      );
    }
    return pingTree;
  }

  _sanitizePingTree(pingTree) {
    const sanitized = { id: pingTree.id };
    if (Object.hasOwn(pingTree, 'name')) {
      sanitized.name = pingTree.name;
    }
    if (Object.hasOwn(pingTree, 'conversionTimerOffset')) {
      sanitized.conversionTimerOffset = pingTree.conversionTimerOffset;
    }
    if (Object.hasOwn(pingTree, 'enabled')) {
      sanitized.enabled = pingTree.enabled;
    }
    if (Object.hasOwn(pingTree, 'isSimalDial')) {
      sanitized.isSimalDial = pingTree.isSimalDial;
    }
    if (Object.hasOwn(pingTree, 'schedule')) {
      sanitized.schedule = this._.cloneDeep(pingTree.schedule);
    }
    if (Object.hasOwn(pingTree, 'revenueSettings')) {
      sanitized.revenueSettings = this._.cloneDeep(pingTree.revenueSettings);
    }
    if (Object.hasOwn(pingTree, 'errorSettings')) {
      sanitized.errorSettings = this._.cloneDeep(pingTree.errorSettings);
    }
    if (Object.hasOwn(pingTree, 'minRevenueToRoute')) {
      sanitized.minRevenueToRoute = pingTree.minRevenueToRoute;
    }
    if (
      typeof pingTree.schedule !== 'undefined' &&
			pingTree.schedule.callConcurrencyMatrix
    ) {
      sanitized.schedule.callConcurrencyMatrix = this.concurrencyMatrixService.postProcessMatrix(
        sanitized.schedule.callConcurrencyMatrix
      );
    }
    return sanitized;
  }

  async getPingTrees(accountId, getStats, updateStatsObj) {
    const url = `${this.serviceBaseUri}${accountId}/pingtrees`;
    const params = {
      ...(getStats ? { includeStats: true } : undefined)
    };
    try {
      const response = await this.$http({
        url: url,
        method: 'GET',
        params: params
      });
      const pingTrees = updateStatsObj
        ? updateStatsObj.pingTrees
        : response.data.pingTrees;
      if (getStats) {
        this.statsService.normalizeStats(
          pingTrees,
          response.data.stats,
          response.data.caps,
          this.pingTreeTargetService.blockReasonTranslation
        );
      }
      response.data.pingTrees = pingTrees;
      return response;
    } catch (error) {
      return error;
    }
  }

  async getPingTree(accountId, pingTreeId) {
    const url = this._serviceBaseUrl(accountId, pingTreeId);
    try {
      const response = await this.$http({
        url: url,
        method: 'GET'
      });
      return this._transformConcurrencyMatrixFromResponse(response);
    } catch (error) {
      return error;
    }
  }

  async createPingTree(accountId, pingTree) {
    const data = this._sanitizePingTree(pingTree);
    const url = `${this.serviceBaseUri}${accountId}/pingTrees`;
    const response = await this.$http.post(url, data);
    return this._transformConcurrencyMatrixFromResponse(response);
  }

  deletePingTree(accountId, pingTreeId, force) {
    const url = `${this._serviceBaseUrl(
      accountId,
      pingTreeId
    )}?force=${!!force}`;
    return this.$http.delete(url);
  }

  async updatePingTree(accountId, pingTree) {
    const data = this._sanitizePingTree(pingTree);
    const url = this._serviceBaseUrl(accountId, pingTree.id);
    const response = await this.$http.patch(url, data);
    return this._transformConcurrencyMatrixFromResponse(response);
  }

  updatePingTreeProperty(accountId, pingTreeId, item) {
    const url = this._serviceBaseUrl(accountId, pingTreeId);
    return this.$http.patch(url, item);
  }

  addTarget(accountId, pingTreeId, targetId) {
    const url = `${this._serviceBaseUrl(accountId, pingTreeId)}/targets`;
    const data = { targetId: targetId };
    return this.$http.patch(url, data);
  }

  removeTarget(accountId, pingTreeId, targetId) {
    const url = `${this._serviceBaseUrl(
      accountId,
      pingTreeId
    )}/targets/${targetId}`;
    return this.$http.delete(url);
  }

  updateTargets(accountId, pingTreeId, addtargets, removeTargets) {
    const url = `${this._serviceBaseUrl(
      accountId,
      pingTreeId
    )}/targets/bulkUpdate`;
    const data = {
      targetAddIds: addtargets,
      targetRemoveIds: removeTargets
    };
    return this.$http.patch(url, data);
  }

  resetCallCounters(accountId, pingTreeId) {
    const url = `${this._serviceBaseUrl(accountId, pingTreeId)}/resetcount`;
    return this.$http.delete(url);
  }

  getPingTreeReferences(accountId, pingTreeId) {
    const url = `${this._serviceBaseUrl(
      accountId,
      pingTreeId
    )}/InboundReferences`;
    return this.$http.get(url);
  }
}
